import { createUseStyles } from 'react-jss';
import { Spacings } from '@picsart/design-system/foundation/spacings';
import { CornerRadius } from '@picsart/design-system/foundation/cornerRadius';
import { BackgroundColors } from '@picsart/design-system/foundation/backgroundColors';
import { TextColors } from '@picsart/design-system/foundation/textColors';
import { PrimaryColors } from '@picsart/design-system/foundation/primaryColors';
import { FontWeights } from '@picsart/design-system/foundation/fonts';
import { FontSizes } from '@picsart/design-system/foundation/typography';
import { BREAKPOINTS } from '@/styles/theme';

const useStyles = createUseStyles(
  {
    left: {
      display: 'flex',
      alignItems: 'center',
      gap: Spacings.s12,
      flex: 1,
      minWidth: 0, // Allow shrinking below content size
      overflow: 'hidden',
    },
    // Logo styles
    logo: {
      width: 32,
      height: 32,
      flexShrink: 0,
      borderRadius: CornerRadius.r4,
      [`@media (max-width: ${BREAKPOINTS.smallMobile}px)`]: {
        width: 28,
        height: 28,
      },
    },
    // Hide logo on mobile for non-library screens (shows back button + title only)
    logoHiddenOnMobile: {
      [`@media (max-width: ${BREAKPOINTS.desktop - 1}px)`]: {
        display: 'none',
      },
    },
    titleGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: Spacings.s12,
      minWidth: 0, // Allow shrinking
      overflow: 'hidden',
      [`@media (max-width: ${BREAKPOINTS.smallMobile}px)`]: {
        gap: Spacings.s8,
      },
    },
    // Vertical divider between title and tagline - subtle separator
    titleDivider: {
      width: 1,
      height: 18,
      backgroundColor: TextColors.tint1,
      opacity: 0.3,
      flexShrink: 0,
      // Hide on small mobile
      [`@media (max-width: ${BREAKPOINTS.smallMobile}px)`]: {
        display: 'none',
      },
    },
    // Mobile-only button - hidden on desktop via CSS
    backButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 32,
      height: 32,
      borderRadius: CornerRadius.circle,
      border: 'none',
      backgroundColor: 'transparent',
      color: TextColors.base,
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
      WebkitTapHighlightColor: 'transparent',
      '&:hover': {
        backgroundColor: BackgroundColors.tint1,
      },
      '&:active': {
        transform: 'scale(0.95)',
      },
      // Hide on desktop - CSS handles responsive
      [`@media (min-width: ${BREAKPOINTS.desktop}px)`]: {
        display: 'none',
      },
    },
    title: {
      fontSize: FontSizes.s16,
      fontWeight: FontWeights.bold,
      color: TextColors.base,
      margin: 0,
      whiteSpace: 'nowrap',
      flexShrink: 0, // Title never truncates
      [`@media (max-width: ${BREAKPOINTS.smallMobile}px)`]: {
        fontSize: FontSizes.s14,
      },
    },
    // Desktop title - shown only on desktop
    titleDesktop: {
      display: 'none',
      [`@media (min-width: ${BREAKPOINTS.desktop}px)`]: {
        display: 'block',
      },
    },
    // Mobile title - hidden on desktop
    titleMobile: {
      display: 'block',
      [`@media (min-width: ${BREAKPOINTS.desktop}px)`]: {
        display: 'none',
      },
    },
    // Tagline with gradient text (pink to purple, matching app buttons)
    tagline: {
      fontSize: FontSizes.s14,
      fontWeight: FontWeights.semibold,
      margin: 0,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      // Gradient text effect - pink to purple
      background: `linear-gradient(90deg, ${PrimaryColors.base.default} 0%, #9333ea 100%)`,
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      // Hide on small mobile to keep header to 1 line
      [`@media (max-width: ${BREAKPOINTS.smallMobile}px)`]: {
        display: 'none',
      },
    },
    // Tagline shown on desktop only
    taglineDesktop: {
      display: 'none',
      [`@media (min-width: ${BREAKPOINTS.desktop}px)`]: {
        display: 'block',
      },
    },
    // Tagline shown on tablet/medium mobile, hidden on small mobile and desktop
    taglineMobile: {
      display: 'block',
      [`@media (min-width: ${BREAKPOINTS.desktop}px)`]: {
        display: 'none',
      },
      // Hide on small mobile to keep header to 1 line
      [`@media (max-width: ${BREAKPOINTS.smallMobile}px)`]: {
        display: 'none',
      },
    },
  },
  { name: 'header-left' }
);

export default useStyles;
